/**
 * WebSocket segmented-message reassembly (AL-311)
 *
 * The backend (`WebSocketUtils.segmentBase64Message32KB`) splits large replies
 * into a sequence of `MessageSegment32KB` envelopes. Each segment carries:
 *
 *   messageType          = 'MessageSegment32KB'
 *   messageIdReference   = the original request's messageId (the accumulator key)
 *   MessageSegment32KB.part    = 1-based index
 *   MessageSegment32KB.parts   = total count
 *   MessageSegment32KB.payload = base64 slice of the *base64-encoded* full JSON
 *   Status.statusCode          = 1099 per segment
 *
 * Because the server base64-encodes the full JSON payload first and slices the
 * resulting string afterwards, individual slices are only independently
 * decodable when the slice boundary happens to land on a base64 quantum (a
 * multiple of 4 characters). To be safe regardless of the block size, we
 * concatenate every slice in part order **first** and decode the joined string
 * **once**. This is the ordering the ticket calls out as load-bearing.
 *
 * This assembler additionally handles the cases the `arcaml-ui` port does not:
 *   - out-of-order arrival (buffered by part index, not arrival order)
 *   - duplicate segments (idempotent per part index)
 *   - bounded lifetime (explicit eviction on completion, timeout, socket close)
 */

/** Shape of the nested `MessageSegment32KB` payload on a segment envelope. */
export interface MessageSegment {
	part: number;
	parts: number;
	payload: string;
}

interface SegmentBuffer {
	/** Total number of segments expected (from `parts`). */
	expectedParts: number;
	/** part index (1-based) -> base64 slice. Deduplicated by index. */
	chunks: Map<number, string>;
}

/**
 * Decode a base64 string to a UTF-8 JavaScript string.
 *
 * `atob` alone yields a Latin1 "binary string"; re-interpreting its bytes
 * through `TextDecoder` recovers multi-byte UTF-8 characters correctly.
 */
export function decodeBase64Utf8(base64: string): string {
	const binary = atob(base64);
	const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
	return new TextDecoder('utf-8').decode(bytes);
}

/**
 * Accumulates `MessageSegment32KB` slices keyed by the original request's
 * `messageIdReference` and reassembles the original JSON payload once every
 * segment has arrived.
 */
export class MessageSegmentAssembler {
	private buffers: Map<string, SegmentBuffer> = new Map();

	/** Whether any segments are currently buffered for the given reference. */
	has(reference: string): boolean {
		return this.buffers.has(reference);
	}

	/**
	 * Add a segment. Buffers by `part` index so out-of-order and duplicate
	 * arrivals are handled correctly.
	 *
	 * @returns `true` once every expected part has been received.
	 */
	add(reference: string, segment: MessageSegment): boolean {
		const { part, parts, payload } = segment;

		// Guard against malformed segment metadata.
		if (!Number.isInteger(part) || !Number.isInteger(parts) || parts < 1 || part < 1 || part > parts) {
			return false;
		}

		let buffer = this.buffers.get(reference);
		if (!buffer) {
			buffer = { expectedParts: parts, chunks: new Map() };
			this.buffers.set(reference, buffer);
		}

		// Trust the latest `parts` count in case an earlier segment was malformed.
		buffer.expectedParts = parts;
		buffer.chunks.set(part, payload ?? '');

		return buffer.chunks.size === buffer.expectedParts;
	}

	/**
	 * Reassemble the buffered slices for a reference into the original decoded
	 * string. Concatenates in ascending part order, then base64-decodes once.
	 *
	 * Returns `null` if the buffer is missing or incomplete.
	 */
	assemble(reference: string): string | null {
		const buffer = this.buffers.get(reference);
		if (!buffer || buffer.chunks.size !== buffer.expectedParts) {
			return null;
		}

		let joined = '';
		for (let part = 1; part <= buffer.expectedParts; part++) {
			const chunk = buffer.chunks.get(part);
			if (chunk === undefined) {
				return null; // gap in sequence — not actually complete
			}
			joined += chunk;
		}

		return decodeBase64Utf8(joined);
	}

	/** Drop the buffer for a single reference. */
	evict(reference: string): void {
		this.buffers.delete(reference);
	}

	/** Drop every buffer (e.g. on socket close). */
	clear(): void {
		this.buffers.clear();
	}
}
